import { makeAutoObservable, runInAction } from 'mobx';

import { NotificationPriority } from '../enums';
import { guid } from '../helpers';
import { RootStore } from '.';

export interface NotificationItem<T = void> {
  id: string;
  message: string;
  data?: T;
  priority: NotificationPriority;
  settings: Partial<NotificationSettings>;
}

interface NotificationSettings {
  gap: number;
  duration: number;
}

// Constructor-only options: the per-item timing settings plus queue lifecycle initialization.
// `startPaused` is kept out of NotificationSettings so it never leaks into per-item settings.
interface NotificationOptions extends NotificationSettings {
  startPaused: boolean;
}

// Gap enforced before/after a High priority notification, unless explicitly overridden
const HIGH_PRIORITY_GAP = 5_000;

export class NotificationStore<T = void> {

  private rootStore: RootStore;

  // 1. The Observable State: Only this triggers React to re-render
  public activeNotification: NotificationItem<T> | null = null;
  
  // 2. Internal State: No need to make these observable
  private queue: NotificationItem<T>[] = [];
  private isProcessing: boolean = false;
  private paused: boolean = false;
  private settings: NotificationSettings = { gap: 1_000, duration: 4_000 };
  private timers: { display?: NodeJS.Timeout; cooldown?: NodeJS.Timeout } = {};

  constructor(rootStore: RootStore, options: Partial<NotificationOptions> = {}) {
    const { startPaused = false, ...settings } = options;
    this.rootStore = rootStore;
    this.paused = startPaused;
    this.settings = { ...this.settings, ...settings };
    makeAutoObservable(this, {
      // Explicitly tell MobX not to track internal FSM properties
      // We only care when `activeNotification` changes
    });
  }

  // The public method your other stores/services will call
  enqueue = (
    message: string,
    data?: T,
    priority: NotificationPriority = NotificationPriority.Standard
  ) => {
    // High priority notifications get a bigger default gap, unless the caller overrides it
    const settings = priority === NotificationPriority.High
      ? { ...this.settings, gap: HIGH_PRIORITY_GAP }
      : this.settings;

    const item: NotificationItem<T> = {
      id: guid(),
      message,
      data,
      priority,
      settings,
    };

    if (priority === NotificationPriority.High) {
      // Push above (before) all Standard notifications, but after any other High ones already queued
      const insertIndex = this.queue.findIndex((queued) => queued.priority !== NotificationPriority.High);
      if (insertIndex === -1) {
        this.queue.push(item);
      } else {
        this.queue.splice(insertIndex, 0, item);
      }
    } else {
      this.queue.push(item);
    }

    this.processQueue();
  };

  // While paused, queued notifications are held back (not dropped); an already
  // visible notification finishes its display normally
  setPaused = (paused: boolean) => {
    if (this.paused === paused) return;
    this.paused = paused;
    if (!paused) this.processQueue();
  };

  private processQueue = () => {
    // Exit if paused, already processing or queue is empty
    if (this.paused || this.isProcessing || this.queue.length === 0) return;

    this.isProcessing = true;
    const nextItem = this.queue.shift()!;

    // Make the notification visible
    this.activeNotification = nextItem;

    // Phase 1: Display for 4 seconds
    this.timers.display = setTimeout(() => {
      
      // We must wrap async state mutations in runInAction
      runInAction(() => {
        this.activeNotification = null;
      });

      // Phase 2: Mandatory cooldown gap, controlled by the *upcoming* notification
      // (falls back to the item that just finished if the queue is empty)
      const gap = this.queue[0]?.settings.gap ?? nextItem.settings.gap;
      this.timers.cooldown = setTimeout(() => {
        this.isProcessing = false;
        this.processQueue(); // Check for next item recursively
      }, gap);

    }, nextItem.settings.duration);
  };

  // Immediately hides the currently displayed notification and proceeds to the cooldown phase
  hideActiveNotification = () => {
    if (!this.activeNotification) return;

    clearTimeout(this.timers.display);
    this.activeNotification = null;

    this.timers.cooldown = setTimeout(() => {
      this.isProcessing = false;
      this.processQueue();
    }, this.settings.gap);
  };

  // Useful if you ever need to forcefully clear the queue (e.g., user logout)
  clearQueue = () => {
    this.queue = [];
    clearTimeout(this.timers.display);
    clearTimeout(this.timers.cooldown);
    this.isProcessing = false;
    this.activeNotification = null;
  };
}
